/**
===================================================
Module: frontend.src.components.visualizer.KeyframeCarousel.tsx
Description: Keyframe carousel component for navigating through keyframes
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useRef, useEffect, useCallback } from "react";
import { useVisualizer } from "../../contexts/VisualizerContext";
import { VISUALIZER_CONFIG } from "../../types/visualizer";

const KeyframeCarousel: React.FC = () => {
  const { state, navigateToKeyframe, refreshUrls } = useVisualizer();
  const { sessionData, currentKeyframeIndex } = state;
  const thumbContainerRef = useRef<HTMLDivElement>(null);

  // Navigation functions
  const navigatePrevious = useCallback(() => {
    if (currentKeyframeIndex > 0) {
      navigateToKeyframe(currentKeyframeIndex - 1);
    }
  }, [currentKeyframeIndex, navigateToKeyframe]);

  const navigateNext = useCallback(() => {
    if (
      sessionData &&
      currentKeyframeIndex < sessionData.keyframes.length - 1
    ) {
      navigateToKeyframe(currentKeyframeIndex + 1);
    }
  }, [currentKeyframeIndex, sessionData, navigateToKeyframe]);

  // Update carousel display
  const updateCarousel = useCallback(
    (selectedIndex: number) => {
      const thumbContainer = thumbContainerRef.current;
      if (!thumbContainer || !sessionData) return;

      // Show current frame and neighbors on each side
      const visibleRange = VISUALIZER_CONFIG.KEYFRAME_CAROUSEL_VISIBLE_RANGE;
      const startIndex = Math.max(0, selectedIndex - visibleRange);
      const endIndex = Math.min(
        sessionData.keyframes.length - 1,
        selectedIndex + visibleRange,
      );

      // Store the navigation buttons before clearing
      const prevButton = thumbContainer.querySelector("#prevKeyframe");
      const nextButton = thumbContainer.querySelector("#nextKeyframe");

      const existingThumbs = thumbContainer.querySelectorAll(".keyframe-thumb");
      existingThumbs.forEach((thumb) => thumb.remove());

      // Add thumbnails for visible range
      for (let i = startIndex; i <= endIndex; i++) {
        const keyframe = sessionData.keyframes[i];
        const thumb = document.createElement("img");
        thumb.className = "keyframe-thumb";
        if (i === selectedIndex) {
          thumb.classList.add("active");
        }
        // Use pre-signed URL for loading
        thumb.src = keyframe.url;
        thumb.setAttribute("data-keyframe-id", keyframe.keyframe_id);
        thumb.alt = `Keyframe ${keyframe.keyframe_id}`;
        thumb.title = `フレーム${keyframe.keyframe_id} (${keyframe.timestamp}秒)`;
        thumb.id = `thumb-${keyframe.keyframe_id}`;
        thumb.crossOrigin = "anonymous";

        thumb.onerror = () => {
          thumb.style.backgroundColor = "#ccc";
          thumb.alt = "Failed to load";
        };

        thumb.addEventListener("click", () => {
          navigateToKeyframe(i);
        });

        thumbContainer.appendChild(thumb);
      }

      if (!thumbContainer.querySelector("#prevKeyframe") && prevButton) {
        thumbContainer.appendChild(prevButton);
      }
      if (!thumbContainer.querySelector("#nextKeyframe") && nextButton) {
        thumbContainer.appendChild(nextButton);
      }
    },
    [sessionData, navigateToKeyframe, refreshUrls],
  );

  // Update carousel when keyframe changes
  useEffect(() => {
    if (sessionData && sessionData.keyframes.length > 0) {
      updateCarousel(currentKeyframeIndex);
    }
  }, [currentKeyframeIndex, sessionData, updateCarousel]);

  // Don't render if no keyframes
  if (!sessionData?.keyframes || sessionData.keyframes.length === 0) {
    return null;
  }

  return (
    <div
      id="keyframeThumbContainer"
      ref={thumbContainerRef}
      className="keyframe-thumb-container"
    >
      <button
        id="prevKeyframe"
        className="carousel-btn"
        onClick={navigatePrevious}
        disabled={currentKeyframeIndex <= 0}
      >
        ◀
      </button>

      <button
        id="nextKeyframe"
        className="carousel-btn"
        onClick={navigateNext}
        disabled={currentKeyframeIndex >= sessionData.keyframes.length - 1}
      >
        ▶
      </button>
    </div>
  );
};

export default KeyframeCarousel;
